
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { calculateMatchStats } from '@/utils/teamMatchStats';
import { Skeleton } from "@/components/ui/skeleton";
import Header from '@/components/layout/Header';
import { TeamDetailsHeader } from '@/components/team-details/header/TeamDetailsHeader';
import { StatsDisplay } from '@/components/team-details/stats-display/StatsDisplay';
import { useTeamSeasons } from '@/hooks/useTeamSeasons';
import { useTeamOpponents } from '@/hooks/useTeamOpponents';
import { useTeamMatches } from '@/hooks/useTeamMatches';

const TeamDetails = () => {
  const { teamName } = useParams<{ teamName: string }>();

  const { data: savedStates, isLoading: isLoadingSavedStates } = useQuery({
    queryKey: ['saved-states'],
    queryFn: async () => {
      console.log('Fetching saved states...');
      const { data, error } = await supabase
        .from('saved_states')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching saved states:', error);
        throw error;
      }
      
      console.log('Fetched saved states:', data);
      
      return data?.map(item => ({
        ...item,
        data: Array.isArray(item.data) 
          ? item.data.map((match: any) => ({
              HomeTeam: String(match.HomeTeam || ''),
              AwayTeam: String(match.AwayTeam || ''),
              ...match
            }))
          : []
      }));
    }
  });

  const {
    selectedSeasons,
    setSelectedSeasons,
    availableSeasonsWithLeagues,
    handleSeasonToggle
  } = useTeamSeasons(teamName, savedStates);

  const opponents = useTeamOpponents(savedStates, teamName);

  const { data: matches, isLoading: isLoadingMatches } = useTeamMatches(
    teamName,
    selectedSeasons,
    savedStates
  );

  const teamStats = useMemo(() => {
    if (!matches || matches.length === 0) return null;
    return calculateMatchStats(matches, teamName || '');
  }, [matches, teamName]);

  const currentLeagueInfo = useMemo(() => {
    if (!selectedSeasons.length || !availableSeasonsWithLeagues.length) return null;
    
    const currentSeason = availableSeasonsWithLeagues.find(
      season => season.season === selectedSeasons[0]
    );
    
    console.log('Current league info:', currentSeason);
    return currentSeason;
  }, [selectedSeasons, availableSeasonsWithLeagues]);

  if (!teamName) {
    return (
      <>
        <Header />
        <div className="container mx-auto py-8">
          <h2 className="text-xl font-semibold mb-2">Team not found</h2>
          <p className="text-gray-600">Please select a valid team.</p>
        </div>
      </>
    );
  }

  if (isLoadingSavedStates || isLoadingMatches) {
    return (
      <>
        <Header />
        <div className="container mx-auto py-8 space-y-8">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container mx-auto py-8">
        <TeamDetailsHeader
          teamName={teamName}
          availableSeasonsWithLeagues={availableSeasonsWithLeagues}
          selectedSeasons={selectedSeasons}
          onSeasonToggle={handleSeasonToggle}
          onClearSelection={() => setSelectedSeasons([])}
        />

        {(!matches || matches.length === 0) ? (
          <div className="text-center py-8">
            <h2 className="text-xl font-semibold mb-2">No data available</h2>
            <p className="text-gray-600">
              Please select at least one season to view statistics for {teamName}
            </p>
          </div>
        ) : (
          <StatsDisplay
            teamStats={teamStats!}
            teamName={teamName}
            matches={matches}
            opponents={opponents}
            selectedSeasons={selectedSeasons}
            league={currentLeagueInfo?.league || ''}
            country={currentLeagueInfo?.country || ''}
          />
        )}
      </div>
    </>
  );
};

export default TeamDetails;
